import type {
    SchemaChatResolver_GetChats,
    SchemaPartnerResolver_GetMyPartnerContact,
} from 'cpool-tables/backend-resolvers';
import { deriveChatId } from 'cpool-utils';

import { NUMBER_SWITCH_TARGET_KINDS } from '~/features/whatsapp/constants/numberSwitchTarget';
import { isPartnerChat } from '~/features/whatsapp/utils/isPartnerChat';
import type { ApplicantChatResolution } from '~/features/whatsapp/utils/resolveApplicantChats';

type Chat = SchemaChatResolver_GetChats[number];

export type NumberSwitchTarget =
    | { kind: typeof NUMBER_SWITCH_TARGET_KINDS.OPEN_CHAT; chatId: string }
    | { kind: typeof NUMBER_SWITCH_TARGET_KINDS.START_APPLICANT_CHAT; slot: 1 | 2 }
    | { kind: typeof NUMBER_SWITCH_TARGET_KINDS.START_PARTNER_CHAT; partnerId: string; phoneE164: string };

// Open the existing own-number chat or start one; null when the chat matches no applicant slot or partner contact.
export const resolveNumberSwitchTarget = ({
    applicantResolution,
    chat,
    chats,
    ownPhoneNumberId,
    partnerContacts,
}: {
    applicantResolution: ApplicantChatResolution | null;
    chat: Chat;
    chats: Array<Chat>;
    ownPhoneNumberId: string | null | undefined;
    partnerContacts: Array<Pick<SchemaPartnerResolver_GetMyPartnerContact, 'id' | 'phoneE164'>>;
}): NumberSwitchTarget | null => {
    if (!ownPhoneNumberId || chat.phoneNumberId === ownPhoneNumberId) return null;

    const applicantSlots = applicantResolution
        ? ([
              [1, applicantResolution.kn1],
              [2, applicantResolution.kn2],
          ] as const)
        : [];
    const applicantMatch = applicantSlots.find(([, slot]) => slot?.chatIds.includes(chat.id));
    if (applicantMatch) {
        const [slotNumber, slot] = applicantMatch;
        if (slot?.chatId && !slot.isForeignNumber) {
            return { chatId: slot.chatId, kind: NUMBER_SWITCH_TARGET_KINDS.OPEN_CHAT };
        }
        return { kind: NUMBER_SWITCH_TARGET_KINDS.START_APPLICANT_CHAT, slot: slotNumber };
    }

    const partnerContact = isPartnerChat(chat)
        ? partnerContacts.find((contact) => chat.partnerIds.includes(contact.id) && contact.phoneE164 === chat.phone)
        : undefined;
    if (partnerContact) {
        const ownNumberChatId = deriveChatId(ownPhoneNumberId, partnerContact.phoneE164);
        if (chats.some((entry) => entry.id === ownNumberChatId)) {
            return { chatId: ownNumberChatId, kind: NUMBER_SWITCH_TARGET_KINDS.OPEN_CHAT };
        }
        return {
            kind: NUMBER_SWITCH_TARGET_KINDS.START_PARTNER_CHAT,
            partnerId: partnerContact.id,
            phoneE164: partnerContact.phoneE164,
        };
    }

    return null;
};
